import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  DEFAULT_FREE_PLAN,
  allowanceLabel,
  formatMinor,
  quotePlan,
  type BillingCurrency,
  type FreePlan,
} from "@talenthop/shared";
import { Check, Info, Eye, EyeOff, Mail, RefreshCw } from "../components/icons.js";
import { api, ApiError } from "../lib/api.js";
import { AuthBrand } from "./Login.js";

/** One INR plan a paid signup can pick — GET /signup/options (0097). Prices in paise, GST-inclusive. */
interface SignupPlan {
  id: string;
  name: string;
  description: string | null;
  currency: BillingCurrency;
  priceMinor: number;
  compareAtMinor: number | null;
  jobs: number;
  resumes: number;
  interviews: number;
  isCustom: boolean;
  isFeatured: boolean;
  validityMonths: number;
  minQty: number | null;
  maxQty: number | null;
}
interface SignupOptions {
  freePlan: FreePlan;
  plans: SignupPlan[];
}

const TERMS_URL = "https://talenthopai.com/terms";
const PRIVACY_URL = "https://talenthopai.com/privacy";
/** POST /signup accepts quantity 1..50 (signupSchema); a plan's own min/max narrows that. */
const QTY_FLOOR = 1;
const QTY_CEIL = 50;

function allowanceLine(a: { jobs: number; resumes: number; interviews: number }): string {
  return [allowanceLabel("job", a.jobs), allowanceLabel("scan", a.resumes), allowanceLabel("interview", a.interviews)].join(
    " · ",
  );
}

function qtyBounds(p: SignupPlan): { min: number; max: number } {
  const min = Math.max(QTY_FLOOR, p.minQty ?? QTY_FLOOR);
  const max = Math.max(min, Math.min(QTY_CEIL, p.maxQty ?? QTY_CEIL));
  return { min, max };
}

/**
 * Public self-serve signup (0097) — one form, two doors:
 *   ?plan=free              → the FREE plan (allowance from GET /signup/options), pending a quick
 *                             human review after email verification.
 *   ?plan=paid[&planId=…]   → pick a plan + quantity; pay right after verifying the email, then
 *                             the account activates on payment.
 * Legacy pricing links (?plan=paid&credits=N) simply land on the plan picker — credits are no
 * longer sold. Every allowance and price on this page comes from the API; nothing is hard-coded.
 * No session is minted here — POST /signup creates the account and queues a verification email.
 */
export function Signup(): ReactNode {
  const [params] = useSearchParams();
  const [plan, setPlan] = useState<"free" | "paid">(params.get("plan") === "paid" ? "paid" : "free");
  const [planId, setPlanId] = useState<string | null>(params.get("planId"));
  const [quantity, setQuantity] = useState(1);

  const [orgName, setOrgName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ["signup-options"],
    queryFn: ({ signal }) => api.get<SignupOptions>("/signup/options", signal),
    retry: 1,
    staleTime: 5 * 60_000,
  });

  // While the options load (or if they can't), the free-plan copy uses the same seed the server
  // falls back to — so the text is never blank and never invents a number of its own.
  const freePlan = options.data?.freePlan ?? DEFAULT_FREE_PLAN;
  const plans = useMemo(() => (options.data?.plans ?? []).filter((p) => !p.isCustom), [options.data]);
  const selected = plans.find((p) => p.id === planId) ?? null;

  // Pick a sensible default once plans arrive: the ?planId= one if valid, else the featured one.
  useEffect(() => {
    if (plans.length === 0) return;
    if (planId && plans.some((p) => p.id === planId)) return;
    setPlanId((plans.find((p) => p.isFeatured) ?? plans[0]!).id);
  }, [plans, planId]);

  // Keep the quantity inside the selected plan's bounds.
  useEffect(() => {
    if (!selected) return;
    const { min, max } = qtyBounds(selected);
    setQuantity((q) => Math.min(max, Math.max(min, q)));
  }, [selected]);

  const quote = selected
    ? quotePlan({
        unitPriceMinor: selected.priceMinor,
        quantity,
        allowances: { jobs: selected.jobs, resumes: selected.resumes, interviews: selected.interviews },
        coupon: null,
        // Plan prices are GST-inclusive (FOUNDER_PACK §5): the total is price × qty; the GST share
        // is split out on the payment step, where the server's quote carries the rate.
        taxPct: 0,
        taxInclusive: true,
      })
    : null;

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean }>("/signup", {
        orgName: orgName.trim(),
        name: name.trim(),
        email: email.trim(),
        password,
        plan,
        ...(plan === "paid" && selected ? { planId: selected.id, quantity } : {}),
        acceptTerms: true,
      }),
  });

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;

  /** The first thing standing between the user and the submit button — shown under it. */
  const blocker: string | null =
    plan === "paid" && !selected
      ? options.isError
        ? "Plans couldn't load — retry above, or start on the free plan."
        : "Choose a plan to continue."
      : orgName.trim().length < 2
        ? "Enter your company name to continue."
        : name.trim().length < 1
          ? "Enter your name to continue."
          : !emailOk
            ? "Enter a valid work email to continue."
            : password.length < 8
              ? "Choose a password of at least 8 characters."
              : confirm !== password
                ? "Confirm your password to continue."
                : !acceptTerms
                  ? "Accept the terms to continue."
                  : null;
  const canSubmit = blocker === null && !submit.isPending;

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setError(null);
    if (!canSubmit) return;
    submit.mutate(undefined, {
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          setError("This email already has a TalentHop account — sign in or reset your password.");
        } else if (err instanceof ApiError && err.status === 404 && plan === "paid") {
          setError("That plan isn't available right now. Pick another plan and try again.");
          void options.refetch();
        } else if (err instanceof ApiError && err.status === 400) {
          setError("Check the form — something didn't validate.");
        } else {
          setError("Couldn't create your account just now. Please retry in a moment.");
        }
      },
    });
  };

  if (submit.isSuccess) {
    return (
      <div className="auth-wrap t1-auth">
        <AuthBrand />
        <div className="auth-card">
          <div className="auth-head">
            <h1>Check your email</h1>
            <p>One click and you're on your way.</p>
          </div>
          <div className="t1-signup-sent">
            <span className="t1-signup-sent-ico" aria-hidden="true">
              <Mail />
            </span>
            <div>
              <b>We sent a verification link to</b>
              <span className="t1-signup-sent-email">{email.trim()}</span>
            </div>
          </div>
          <p className="auth-hint t1-signup-next">
            {plan === "free"
              ? "Verify your email, then our team gives your request a quick review (usually within one working day) and we'll email you when your workspace is ready."
              : `Verify your email, then pay for ${selected ? `${selected.name}${quantity > 1 ? ` × ${quantity}` : ""}` : "your plan"} and your workspace starts right away.`}
          </p>
          <p className="auth-hint">Didn't get it? Check spam, or wait a minute — it can take a moment to arrive.</p>
          <p className="auth-hint" style={{ marginTop: 14 }}>
            Already verified? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrap t1-auth t1-signup">
      <AuthBrand />
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <div className="auth-head">
          <h1>Create your TalentHop account</h1>
          <p>Start free, or buy a plan and start right away.</p>
        </div>

        <div className="t1-signup-seg" role="radiogroup" aria-label="Plan">
          <button
            type="button"
            role="radio"
            aria-checked={plan === "free"}
            className={plan === "free" ? "on" : ""}
            onClick={() => setPlan("free")}
          >
            Free plan
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={plan === "paid"}
            className={plan === "paid" ? "on" : ""}
            onClick={() => setPlan("paid")}
          >
            Buy a plan
          </button>
        </div>

        {plan === "free" ? (
          <div className="t1-signup-free" aria-busy={options.isLoading}>
            <b>
              {allowanceLine(freePlan)} · free for {freePlan.validityDays} {freePlan.validityDays === 1 ? "day" : "days"}
            </b>
            <p>
              A quick review by our team (usually within one working day). Adaptive follow-ups and resume-tailored
              questions come with any paid plan.
            </p>
          </div>
        ) : (
          <div className="t1-signup-paid">
            {options.isLoading && <p className="auth-hint">Loading plans…</p>}
            {options.isError && (
              <div className="t1-signup-plans-err" role="alert">
                <span>Plans couldn't load — try again.</span>
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => void options.refetch()}
                  disabled={options.isFetching}
                >
                  <RefreshCw /> {options.isFetching ? "Retrying…" : "Retry"}
                </button>
              </div>
            )}
            {options.isSuccess && plans.length === 0 && (
              <p className="auth-hint">No plans are on sale right now — start on the free plan, or try again later.</p>
            )}
            {plans.length > 0 && (
              <div className="t1-signup-plans" role="radiogroup" aria-label="Choose a plan">
                {plans.map((p) => {
                  const on = p.id === planId;
                  const was = p.compareAtMinor != null && p.compareAtMinor > p.priceMinor;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      className={`t1-signup-plan${on ? " on" : ""}${p.isFeatured ? " featured" : ""}`}
                      onClick={() => setPlanId(p.id)}
                    >
                      <span className="t1-signup-plan-top">
                        <b>{p.name}</b>
                        {p.isFeatured && <span className="pill v">Most popular</span>}
                      </span>
                      <span className="t1-signup-plan-price">
                        <b className="tnum">{formatMinor(p.priceMinor, p.currency)}</b>
                        {was && (
                          <s className="tnum" aria-label={`was ${formatMinor(p.compareAtMinor!, p.currency)}`}>
                            {formatMinor(p.compareAtMinor!, p.currency)}
                          </s>
                        )}
                        <span className="t1-signup-plan-tax">incl. GST</span>
                      </span>
                      <span className="t1-signup-plan-meta">{allowanceLine(p)}</span>
                      <span className="t1-signup-plan-meta">
                        Valid {p.validityMonths} {p.validityMonths === 1 ? "month" : "months"}
                      </span>
                      <span className="t1-signup-plan-tick" aria-hidden="true">
                        <Check />
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {selected && quote && (
              <div className="t1-signup-qty">
                <div className="t1-signup-qty-label">
                  <span>Quantity</span>
                  <small>{allowanceLine(quote.allowances)}</small>
                </div>
                <div className="t1-signup-stepper">
                  <button
                    type="button"
                    aria-label="Fewer"
                    data-tip="Fewer"
                    disabled={quantity <= qtyBounds(selected).min}
                    onClick={() => setQuantity((q) => q - 1)}
                  >
                    −
                  </button>
                  <output aria-live="polite" className="tnum">
                    {quantity}
                  </output>
                  <button
                    type="button"
                    aria-label="More"
                    data-tip="More"
                    disabled={quantity >= qtyBounds(selected).max}
                    onClick={() => setQuantity((q) => q + 1)}
                  >
                    +
                  </button>
                </div>
                <div className="t1-signup-total">
                  <span>Total</span>
                  <b className="tnum">{formatMinor(quote.totalMinor, selected.currency)}</b>
                  <small>incl. GST</small>
                </div>
              </div>
            )}
            <p className="t1-signup-note">Paid plans include every feature and start immediately after payment.</p>
          </div>
        )}

        {error && (
          <div className="banner crit" style={{ marginBottom: 16 }}>
            <Info />
            <span>{error}</span>
          </div>
        )}

        <div className="field">
          <label htmlFor="org-name">Company name</label>
          <input
            id="org-name"
            className="input"
            type="text"
            autoComplete="organization"
            required
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="Acme Talent Pvt. Ltd."
          />
        </div>

        <div className="field">
          <label htmlFor="signup-name">Your name</label>
          <input
            id="signup-name"
            className="input"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Priya Sharma"
          />
        </div>

        <div className="field">
          <label htmlFor="signup-email">
            Work email <span className="hint">— use your work email</span>
          </label>
          <input
            id="signup-email"
            className="input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
        </div>

        <div className="field">
          <label htmlFor="signup-password">
            Password <span className="hint">— at least 8 characters</span>
          </label>
          <div className="password-field">
            <input
              id="signup-password"
              className="input"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => setCapsOn(e.getModifierState("CapsLock"))}
              onKeyUp={(e) => setCapsOn(e.getModifierState("CapsLock"))}
              onBlur={() => setCapsOn(false)}
              placeholder="••••••••"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide passwords" : "Show passwords"}
              aria-pressed={showPassword}
              data-tip={showPassword ? "Hide passwords" : "Show passwords"}
              data-tip-pos="left"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff /> : <Eye />}
            </button>
          </div>
          {capsOn && (
            <p className="auth-hint" role="status" style={{ marginTop: 6, textAlign: "left", color: "var(--warn)" }}>
              Caps Lock is on.
            </p>
          )}
          {tooShort && (
            <p className="auth-hint" style={{ textAlign: "left", marginTop: 6, color: "var(--crit)" }}>
              Use at least 8 characters.
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor="signup-confirm">Confirm password</label>
          <input
            id="signup-confirm"
            className="input"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onKeyDown={(e) => setCapsOn(e.getModifierState("CapsLock"))}
            onKeyUp={(e) => setCapsOn(e.getModifierState("CapsLock"))}
            onBlur={() => setCapsOn(false)}
            placeholder="••••••••"
          />
          {mismatch && (
            <p className="auth-hint" style={{ textAlign: "left", marginTop: 6, color: "var(--crit)" }}>
              Those passwords don't match.
            </p>
          )}
        </div>

        <label className="t1-signup-terms">
          <input
            type="checkbox"
            required
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
          />
          <span>
            I agree to the{" "}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </span>
        </label>

        <button
          className="btn primary"
          type="submit"
          style={{ width: "100%", justifyContent: "center", marginTop: 4, gap: 8 }}
          aria-busy={submit.isPending}
          aria-describedby={blocker ? "signup-blocker" : undefined}
          disabled={!canSubmit}
        >
          {submit.isPending && <span className="btn-spin" aria-hidden="true" />}
          {submit.isPending ? "Creating account…" : plan === "paid" ? "Create account" : "Create free account"}
        </button>
        {blocker && !submit.isPending && (
          <p id="signup-blocker" className="t1-signup-blocker" role="status">
            {blocker}
          </p>
        )}

        <p className="auth-hint t1-signup-foot">
          Already have an account?{" "}
          {/* a paid-intent visitor who signs in instead lands on Plans & usage (Login's buy intent) */}
          <Link to={plan === "paid" ? "/login?buyCredits=1" : "/login"}>Sign in</Link>
        </p>
      </form>
    </div>
  );
}
